// src/utils/helper.ts
import { useTranslation } from 'react-i18next';
import { Project, FileType, ProjectStatus } from '../types';
import * as XLSX from 'xlsx';

const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB in bytes
const ALLOWED_FILE_TYPES = new Set(['.dwg', '.pdf', '.png', '.jpg', '.jpeg']);
// src/utils/helper.ts
// Remove this import
// import { useTranslation } from 'react-i18next';

// Modify the validateFile function to not use hooks
export const validateFile = (
  file: File,
  errorMessages = {
    sizeExceeded: (size: string) => `File size exceeds 20MB limit (${size}MB)`,
    typeNotSupported:
      'File type not supported. Allowed types: DWG, PDF, PNG, JPEG',
  }
): { isValid: boolean; error: string } => {
  if (file.size > MAX_FILE_SIZE) {
    return {
      isValid: false,
      error: errorMessages.sizeExceeded((file.size / (1024 * 1024)).toFixed(2)),
    };
  }

  const extension = file.name
    .toLowerCase()
    .substring(file.name.lastIndexOf('.'));
  if (!ALLOWED_FILE_TYPES.has(extension)) {
    return {
      isValid: false,
      error: errorMessages.typeNotSupported,
    };
  }

  return { isValid: true, error: '' };
};

export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getFileType = (fileName: string): FileType => {
  const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
  switch (extension) {
    case '.dwg':
      return 'DWG';
    case '.pdf':
      return 'PDF';
    case '.png':
      return 'PNG';
    case '.jpg':
    case '.jpeg':
      return 'JPEG';
    default:
      throw new Error('Unsupported file type');
  }
};

export const shouldArchive = (lastStatusChange: string): boolean => {
  const oneYearAgo = new Date();
  oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
  return new Date(lastStatusChange) < oneYearAgo;
};

export const filterProjects = (
  projects: Project[],
  filters: {
    client?: string;
    status?: ProjectStatus;
    dateFrom?: string;
    dateTo?: string;
    searchTerm?: string;
    projectId?: string;
  }
): Project[] => {
  return projects.filter((project) => {
    const matchesClient =
      !filters.client ||
      project.client.toLowerCase().includes(filters.client.toLowerCase());

    const matchesStatus = !filters.status || project.status === filters.status;

    const matchesDateRange =
      (!filters.dateFrom ||
        new Date(project.createdAt) >= new Date(filters.dateFrom)) &&
      (!filters.dateTo ||
        new Date(project.createdAt) <= new Date(filters.dateTo));

    const matchesSearch =
      !filters.searchTerm ||
      project.name.toLowerCase().includes(filters.searchTerm.toLowerCase()) ||
      project.id.toLowerCase().includes(filters.searchTerm.toLowerCase());

    const matchesProjectId =
      !filters.projectId ||
      project.id.toLowerCase().includes(filters.projectId.toLowerCase());

    return (
      matchesClient &&
      matchesStatus &&
      matchesDateRange &&
      matchesSearch &&
      matchesProjectId
    );
  });
};

export const sortProjects = (
  projects: Project[],
  sort: {
    field: keyof Project;
    direction: 'asc' | 'desc';
  }
): Project[] => {
  return [...projects].sort((a, b) => {
    const aValue = a[sort.field];
    const bValue = b[sort.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sort.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return 0;
  });
};

export const paginateProjects = (
  projects: Project[],
  page: number,
  pageSize: number
): Project[] => {
  const start = (page - 1) * pageSize;
  return projects.slice(start, start + pageSize);
};

export const exportToExcel = (projects: Project[]): void => {
  // Prepare data for export
  const data = projects.map((project) => ({
    'Project ID': project.id,
    Name: project.name,
    Client: project.client,
    Location: project.location,
    Status: project.status,
    'Created By': project.createdByUser,
    'Created Date': new Date(project.createdAt).toLocaleDateString(),
    'Last Modified': new Date(project.lastModified).toLocaleDateString(),
    'Number of Attachments': project.attachments.length,
    Description: project.description,
  }));

  // Create workbook and worksheet
  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(data);

  // Add column widths
  const colWidths = [
    { wch: 15 }, // Project ID
    { wch: 30 }, // Name
    { wch: 25 }, // Client
    { wch: 20 }, // Location
    { wch: 12 }, // Status
    { wch: 20 }, // Created By
    { wch: 15 }, // Created Date
    { wch: 15 }, // Last Modified
    { wch: 10 }, // Number of Attachments
    { wch: 40 }, // Description
  ];
  ws['!cols'] = colWidths;

  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, 'Projects');

  // Generate Excel file
  const fileName = `Projects_Export_${
    new Date().toISOString().split('T')[0]
  }.xlsx`;
  XLSX.writeFile(wb, fileName);
};
